/** Read camelCase or PascalCase property from API DTOs. */
export function pickScheduleId(row: any): number {
  var id = pickDto(row, 'id', 'Id');
  if (id === '' || id === null || id === undefined) {
    id = pickDto(row, 'scheduleId', 'ScheduleId');
  }
  return parseInt(String(id || '0'), 10);
}

export function pickDto(row: any, camel: string, pascal: string): any {
  if (!row) {
    return '';
  }
  if (Object.prototype.hasOwnProperty.call(row, camel)) {
    return row[camel];
  }
  return row[pascal];
}

export function formatQty(row: any): string {
  var v = pickDto(row, 'qty', 'Qty');
  if (v === '' || v === null || v === undefined) {
    return '';
  }
  return String(v);
}

export function formatDateOnlyForApi(d: Date): string {
  if (!d) {
    return '';
  }
  var y = d.getFullYear();
  var m = d.getMonth() + 1;
  var day = d.getDate();
  return y + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
}

export function formatFileStamp(d: Date): string {
  var y = d.getFullYear();
  var m = d.getMonth() + 1;
  var day = d.getDate();
  var h = d.getHours();
  var min = d.getMinutes();
  return '' + y + (m < 10 ? '0' : '') + m + (day < 10 ? '0' : '') + day + '_' + (h < 10 ? '0' : '') + h + (min < 10 ? '0' : '') + min;
}

export function buildScheduleFreq(freqPreset: string, customDays: number): string {
  if (freqPreset === 'Custom') {
    return 'Every ' + customDays + ' days';
  }
  return freqPreset;
}

export function isScheduleUrgent(row: any): boolean {
  var dt = pickDto(row, 'dtNext', 'DtNext');
  if (!dt) {
    return false;
  }
  var d = dt instanceof Date ? dt : new Date(dt);
  if (isNaN(d.getTime())) {
    return false;
  }
  var today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d.getTime() <= today.getTime();
}

export function hasScheduleNote(row: any): boolean {
  var n = pickDto(row, 'notes', 'Notes');
  return n != null && String(n).trim() !== '';
}

export function historyStatusLabel(row: any): string {
  return isHistoryAdministered(row) ? 'Administered' : 'Not Administered';
}

export function historyStatusClass(row: any): string {
  if (isHistoryAdministered(row)) {
    return 'ar-hist-admin';
  }
  return 'ar-hist-fail';
}

export function isHistoryAdministered(row: any): boolean {
  var v = pickDto(row, 'administered', 'Administered');
  return v === true || v === 1 || v === '1' || v === 'true';
}

export function patientDisplayName(row: any): string {
  var full = pickDto(row, 'fullName', 'FullName');
  if (full) {
    return full;
  }
  var last = pickDto(row, 'lastName', 'LastName') || '';
  var first = pickDto(row, 'firstName', 'FirstName') || '';
  return (last + ', ' + first).replace(/^,\s*|\s*,\s*$/g, '').trim();
}

export function patientBirthdateText(row: any): string {
  var text = pickDto(row, 'birthdateText', 'BirthdateText');
  if (text) {
    return String(text);
  }
  var bd = pickDto(row, 'birthdate', 'Birthdate');
  if (!bd) {
    return '';
  }
  var d = bd instanceof Date ? bd : new Date(bd);
  if (isNaN(d.getTime()) || d.getFullYear() < 1900) {
    return '';
  }
  var y = d.getFullYear();
  var m = d.getMonth() + 1;
  var day = d.getDate();
  return '' + y + '-' + (m < 10 ? '0' : '') + m + '-' + (day < 10 ? '0' : '') + day;
}

export function patientGenderText(row: any): string {
  var genderText = pickDto(row, 'genderText', 'GenderText');
  if (genderText) {
    return String(genderText);
  }
  var gender = pickDto(row, 'gender', 'Gender');
  if (gender === 'M') {
    return 'Male';
  }
  if (gender === 'F') {
    return 'Female';
  }
  return gender ? String(gender) : '';
}

export function resolveInitialPharmacyId(sessionPharmacyId: number, options: { id: number }[]): number {
  var i = 0;
  for (i = 0; i < options.length; i++) {
    if (options[i].id === sessionPharmacyId) {
      return sessionPharmacyId;
    }
  }
  return options.length > 0 ? options[0].id : 0;
}

export function buildNursingHomeOptions(rows: any[]): string[] {
  var opts = ['All'];
  var seen: { [key: string]: boolean } = {};
  var i = 0;
  for (i = 0; i < rows.length; i++) {
    var nh = pickDto(rows[i], 'nursingHome', 'NursingHome');
    if (nh && !seen[nh]) {
      seen[nh] = true;
      opts.push(nh);
    }
  }
  opts.sort();
  if (opts.length > 1) {
    opts = ['All'].concat(opts.slice(1).sort());
  }
  return opts;
}

export function pagedItems<T>(items: T[], page: number, pageSize: number): T[] {
  if (!items || items.length === 0 || pageSize <= 0) {
    return [];
  }
  var start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize: number): number {
  if (pageSize <= 0 || count <= 0) {
    return 0;
  }
  return Math.max(1, Math.ceil(count / pageSize));
}

export function rxSearchText(rx: any): string {
  return [
    pickDto(rx, 'din', 'Din'),
    pickDto(rx, 'brandName', 'BrandName'),
    pickDto(rx, 'generic', 'Generic'),
    pickDto(rx, 'sig', 'Sig'),
    formatQty(rx)
  ].join(' ').toLowerCase();
}

export function filterRxList(rxList: any[], query: string): any[] {
  if (!rxList || rxList.length === 0) {
    return [];
  }
  var q = (query || '').trim().toLowerCase();
  if (!q) {
    return rxList;
  }
  return rxList.filter(function (rx) {
    return rxSearchText(rx).indexOf(q) >= 0;
  });
}

export function parsePagedResult(result: any): { items: any[]; totalCount: number } {
  var items = result && result.items ? result.items : (result && result.Items ? result.Items : []);
  var totalCount = result && result.totalCount != null
    ? result.totalCount
    : (result && result.TotalCount != null ? result.TotalCount : 0);
  return { items: items || [], totalCount: totalCount };
}
